import type { z } from "zod";
import { problem } from "@duress/shared";

// Empty everywhere now: local dev's Vite dev-server proxy (vite.config.ts)
// and the deployed Cloudflare Pages Functions proxy (functions/_shared/proxy.ts)
// both forward /v1 and /auth to the backend from the same origin the SPA is
// served from, so a relative path always reaches it. This is what makes the
// session cookie a first-party cookie on iOS -- see proxy.ts for why that
// matters. VITE_API_BASE_URL is kept as an override for anyone who needs to
// point a local build at a specific deployed backend.
export const API_BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? "";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: problem.Problem,
  ) {
    super(body.title);
    this.name = "ApiError";
  }
}

// Parses a non-OK response into a typed RFC 7807 problem body, falling back
// to a synthesised one if the response isn't valid JSON or doesn't match the
// shape (e.g. an upstream proxy error page).
export async function parseProblem(res: Response): Promise<problem.Problem> {
  const raw: unknown = await res.json().catch(() => ({
    type: "https://api.duress.com/problems/unknown",
    title: res.statusText,
    status: res.status,
  }));
  const parsed = problem.ProblemSchema.safeParse(raw);
  return parsed.success
    ? parsed.data
    : {
        type: "https://api.duress.com/problems/unknown",
        title: res.statusText,
        status: res.status,
      };
}

// All three z.ZodType type params given explicitly — with just <T>, Input
// still defaults to Output (same problem z.ZodSchema<T> has), which breaks
// inference for any schema using .default()/.transform(), where Input and
// Output legitimately differ (e.g. reporting-period's
// by_status: z.array(...).default([])).
export async function apiFetch<T>(
  path: string,
  schema: z.ZodType<T, z.ZodTypeDef, unknown>,
  init?: RequestInit,
): Promise<T> {
  const headers = new Headers(init?.headers);
  if (!headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const res = await fetch(`${API_BASE_URL}/v1${path}`, {
    ...init,
    credentials: "include",
    headers,
  });

  if (!res.ok) {
    throw new ApiError(res.status, await parseProblem(res));
  }

  const json: unknown = await res.json();
  return schema.parse(json);
}

// Deletes are idempotent — a 404 means the resource is already gone, which we
// treat as a successful outcome rather than an error.
export async function apiDelete(path: string): Promise<void> {
  const res = await fetch(`${API_BASE_URL}/v1${path}`, { method: "DELETE", credentials: "include" });
  if (!res.ok && res.status !== 404) {
    throw new ApiError(res.status, await parseProblem(res));
  }
}
